import React, { useCallback, useMemo } from 'react';

import { FormValues, IJSONSchemaProperty, SchemaType } from '../../integration-test-modal.types';
import { schemaUtils } from '../../integration-test-modal.utils';

import { BooleanField } from './fields/BooleanField';
import { EnumSelectField } from './fields/EnumSelectField';
import { JSONField } from './fields/JSONField';
import { NestedObjectField } from './fields/NestedObjectField';
import { NullableFieldOverlay } from './fields/NullableFieldOverlay';
import { NullFieldDisplay } from './fields/NullFieldDisplay';
import { NumberField } from './fields/NumberField';
import { PasswordField } from './fields/PasswordField';
import { TextField } from './fields/TextField';
import { ISchemaFormFieldProps } from './schema-form-field.types';
import { schemaFieldUtils } from './schema-form-field.utils';

export const SchemaFormField: React.FC<ISchemaFormFieldProps> = ({
  name,
  path,
  property,
  value,
  onChange,
  required,
  definitions,
  hideOptionalFields
}) => {
  const resolvedProperty = useMemo((): IJSONSchemaProperty => {
    if (property.$ref) {
      const refProperty = schemaUtils.resolveRef(property.$ref, definitions);
      if (refProperty) {
        const { $ref: _ref, ...originalPropertyFields } = property;
        return { ...refProperty, ...originalPropertyFields };
      }
    }
    return property;
  }, [property, definitions]);

  const isNullable = useMemo(
    () => schemaFieldUtils.isNullableProperty(resolvedProperty),
    [resolvedProperty]
  );
  const isNull = value === null;

  const handleSetNull = useCallback((): void => {
    onChange(path, null);
  }, [path, onChange]);

  if (hideOptionalFields && !required) {
    return null;
  }

  if (resolvedProperty.isHidden) {
    return null;
  }

  if (isNullable && isNull) {
    return (
      <NullFieldDisplay
        name={name}
        path={path}
        property={resolvedProperty}
        required={required}
        onChange={onChange}
      />
    );
  }

  const wrapNullable = (element: React.ReactElement): React.ReactElement => {
    if (!isNullable) {
      return element;
    }
    return (
      <NullableFieldOverlay onSetNull={handleSetNull}>
        {element}
      </NullableFieldOverlay>
    );
  };

  if (resolvedProperty.type === SchemaType.ARRAY) {
    return wrapNullable(
      <JSONField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (schemaFieldUtils.isNestedObjectField(resolvedProperty)) {
    return wrapNullable(
      <NestedObjectField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value as FormValues}
        onChange={onChange}
        required={required}
        definitions={definitions}
        hideOptionalFields={hideOptionalFields}
      />
    );
  }

  if (resolvedProperty.type === SchemaType.OBJECT) {
    return wrapNullable(
      <JSONField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (resolvedProperty.enum) {
    return wrapNullable(
      <EnumSelectField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (resolvedProperty.type === SchemaType.BOOLEAN) {
    return wrapNullable(
      <BooleanField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (schemaFieldUtils.isNumericField(resolvedProperty)) {
    return wrapNullable(
      <NumberField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (schemaFieldUtils.isPasswordField(resolvedProperty)) {
    return wrapNullable(
      <PasswordField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (schemaFieldUtils.isUnknownObjectField(resolvedProperty, value)) {
    return wrapNullable(
      <JSONField
        name={name}
        path={path}
        property={{ ...resolvedProperty, type: SchemaType.OBJECT }}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  return wrapNullable(
    <TextField
      name={name}
      path={path}
      property={resolvedProperty}
      value={value}
      onChange={onChange}
      required={required}
    />
  );
};
